import { ArrowLeft, Eye, EyeOff, LockKeyhole, Mail, ShieldCheck, UserRound } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "../components/Logo";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

export function AuthPage() {
  const { session, login, registerAccount } = useAuth();
  const [mode, setMode] = useState<"login" | "register">("login");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("admin@calcplan.local");
  const [password, setPassword] = useState("CalcPlan@2026");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoveryToken, setRecoveryToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [recoveryStatus, setRecoveryStatus] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const destination = (location.state as { from?: string } | null)?.from ?? "/dashboard";

  useEffect(() => {
    if (session) navigate(destination, { replace: true });
  }, [destination, navigate, session]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (mode === "register" && password !== passwordConfirmation) {
      setError("As senhas informadas não coincidem.");
      return;
    }
    setLoading(true);
    try {
      if (mode === "register") await registerAccount(displayName, email, password);
      else await login(email, password);
      navigate(destination, { replace: true });
    } catch (reason) {
      setError(reason instanceof ApiError
        ? reason.message
        : mode === "register" ? "Não foi possível criar sua conta." : "Não foi possível entrar.");
    } finally {
      setLoading(false);
    }
  }

  function switchMode(nextMode: "login" | "register") {
    setMode(nextMode);
    setError("");
    setRecoveryStatus("");
    setRecoveryOpen(false);
    setShowPassword(false);
    setPasswordConfirmation("");
    if (nextMode === "register") {
      setEmail("");
      setPassword("");
    } else {
      setEmail("admin@calcplan.local");
      setPassword("CalcPlan@2026");
    }
  }

  async function requestRecovery() {
    setError("");
    setRecoveryStatus("");
    try {
      const response = await api<{ data: { accepted: boolean; developmentToken?: string } }>("/auth/password-reset/request", {
        method: "POST",
        body: { email },
      });
      if (response.data.developmentToken) setRecoveryToken(response.data.developmentToken);
      setRecoveryStatus(response.data.developmentToken ? "Token local gerado. Defina a nova senha." : "Se a conta existir, enviaremos as instruções.");
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Não foi possível solicitar a recuperação.");
    }
  }

  async function confirmRecovery(event: FormEvent) {
    event.preventDefault();
    setError("");
    try {
      await api("/auth/password-reset/confirm", {
        method: "POST",
        body: { token: recoveryToken, password: newPassword },
      });
      setPassword(newPassword);
      setRecoveryOpen(false);
      setRecoveryToken("");
      setNewPassword("");
      setRecoveryStatus("Senha atualizada. Entre com a nova credencial.");
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Não foi possível redefinir a senha.");
    }
  }

  return (
    <main className="auth-page">
      <section className="auth-showcase" aria-label="Apresentação do Calcme 3D">
        <Link className="auth-back" to="/"><ArrowLeft size={18} /> Voltar ao site</Link>
        <div className="auth-showcase__content">
          <Logo inverse />
          <span className="eyebrow eyebrow--dark">Um projeto. Todas as etapas.</span>
          <h1>Da primeira parede ao plano de corte.</h1>
          <p>
            Centralize plantas, modelos, materiais e documentação técnica em
            um fluxo preciso e visual.
          </p>
          <div className="auth-preview" aria-hidden="true">
            <div className="auth-preview__top">
              <span /><span /><span />
            </div>
            <div className="auth-preview__body">
              <div className="auth-preview__rail" />
              <div className="auth-preview__room">
                <div className="room-floor" />
                <div className="room-wall room-wall--a" />
                <div className="room-wall room-wall--b" />
                <div className="room-module room-module--a" />
                <div className="room-module room-module--b" />
              </div>
              <div className="auth-preview__panel" />
            </div>
          </div>
        </div>
        <div className="auth-showcase__trust">
          <ShieldCheck size={18} />
          <span>Seus projetos permanecem isolados no seu workspace.</span>
        </div>
      </section>

      <section className="auth-form-wrap">
        <div className="auth-mobile-logo"><Logo /></div>
        <form className="auth-card" onSubmit={submit}>
          <div>
            <span className="eyebrow">Workspace Calcme 3D</span>
            <h2>{mode === "register" ? "Crie sua conta" : "Que bom ter você de volta"}</h2>
            <p>{mode === "register" ? "Comece em um workspace só seu." : "Entre para continuar seus projetos."}</p>
          </div>
          {mode === "register" && (
            <label>
              Nome
              <span className="input-with-icon">
                <UserRound size={18} />
                <input
                  type="text"
                  autoComplete="name"
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                  minLength={2}
                  maxLength={120}
                  required
                />
              </span>
            </label>
          )}
          <label>
            E-mail
            <span className="input-with-icon">
              <Mail size={18} />
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </span>
          </label>
          <label>
            Senha
            <span className="input-with-icon">
              <LockKeyhole size={18} />
              <input
                type={showPassword ? "text" : "password"}
                autoComplete={mode === "register" ? "new-password" : "current-password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                minLength={mode === "register" ? 12 : 8}
                required
              />
              <button
                type="button"
                className="input-action"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </span>
            {mode === "register" && <small className="auth-password-hint">Use pelo menos 12 caracteres.</small>}
          </label>
          {mode === "register" && (
            <label>
              Confirmar senha
              <span className="input-with-icon">
                <LockKeyhole size={18} />
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={passwordConfirmation}
                  onChange={(event) => setPasswordConfirmation(event.target.value)}
                  minLength={12}
                  required
                />
              </span>
            </label>
          )}
          {mode === "login" && (
            <div className="auth-card__row">
              <label className="check-label"><input type="checkbox" /> Manter conectado</label>
              <button type="button" className="text-button" onClick={() => { setRecoveryOpen(true); setError(""); }}>Esqueci minha senha</button>
            </div>
          )}
          {error && <div className="form-error" role="alert">{error}</div>}
          <button className="button button--primary button--large" disabled={loading}>
            {loading
              ? mode === "register" ? "Criando conta…" : "Entrando…"
              : mode === "register" ? "Criar minha conta" : "Entrar no Calcme 3D"}
          </button>
          {mode === "login" && (
            <p className="demo-hint">Ambiente local: as credenciais de demonstração já estão preenchidas.</p>
          )}
          <p className="auth-mode-switch">
            {mode === "register" ? "Já tem uma conta?" : "Ainda não tem uma conta?"}
            <button type="button" className="text-button" onClick={() => switchMode(mode === "register" ? "login" : "register")}>
              {mode === "register" ? "Entrar" : "Criar conta"}
            </button>
          </p>
        </form>
        {recoveryStatus && !recoveryOpen && <div className="auth-inline-status">{recoveryStatus}</div>}
        {recoveryOpen && (
          <div className="dialog-backdrop">
            <form className="dialog recovery-dialog" role="dialog" aria-modal="true" aria-labelledby="recovery-title" onSubmit={confirmRecovery}>
              <header><div><span className="eyebrow">Segurança</span><h2 id="recovery-title">Redefinir senha</h2></div><button type="button" className="icon-button" onClick={() => setRecoveryOpen(false)} aria-label="Fechar">×</button></header>
              <p>Solicite um token para <strong>{email}</strong>. No ambiente local ele aparece automaticamente.</p>
              <button type="button" className="button button--secondary" onClick={() => void requestRecovery()}>Solicitar token</button>
              {recoveryStatus && <div className="save-success">{recoveryStatus}</div>}
              <label className="field">Token<input value={recoveryToken} onChange={(event) => setRecoveryToken(event.target.value)} autoComplete="one-time-code" /></label>
              <label className="field">Nova senha<input type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} minLength={12} autoComplete="new-password" /></label>
              {error && <div className="form-error" role="alert">{error}</div>}
              <footer><button type="button" className="button button--secondary" onClick={() => setRecoveryOpen(false)}>Cancelar</button><button className="button button--primary" disabled={recoveryToken.length < 20 || newPassword.length < 12}>Atualizar senha</button></footer>
            </form>
          </div>
        )}
        <footer>© 2026 Calcme 3D · <Link to="/privacy">Privacidade</Link> · <Link to="/terms">Termos</Link></footer>
      </section>
    </main>
  );
}
